/**
 * File: src/data/ant-clash-wiki.js
 * Mô tả: Bộ dữ liệu nội dung chi tiết dạng HTML/Structured Data cho Wiki & Hướng dẫn chơi game Ant Clash (Tiếng Anh & Tiếng Việt).
 * Liên quan: src/components/game-wiki.js, src/i18n/i18n-service.js
 */

export const antClashWikiDataEn = [
  {
    id: 'intro',
    title: '📖 Overview & Lore',
    content: `
      <h3>Ant Clash Overview</h3>
      <p><strong>Ant Clash</strong> is a fast-paced 2D real-time strategy (RTS) game. Players command an ant colony fighting against aggressive termite swarms to claim control over nests across the battlefield.</p>
      
      <div style="background: rgba(124, 58, 237, 0.15); border-left: 4px solid var(--color-primary); padding: 1rem; border-radius: 6px; margin: 1rem 0;">
        💡 <strong>Core Gameplay Loop:</strong><br/>
        <code>Select Level → Select Ant Types → Dispatch Army → Accumulate Resin & Cards → Upgrade Colony</code>
      </div>

      <h4>Battle Environments</h4>
      <p>Battles span across 5 unique environments: Morning Dew Garden, Ancient Root Cave, Pebble Creek, Sunset Dune, and Queen's Fortress.</p>
    `,
  },
  {
    id: 'guide',
    title: '⚔️ Controls & Gameplay Guide',
    content: `
      <h3>Army Controls & Strategy</h3>
      <ul>
        <li><strong>Dispatch Units:</strong> Tap your nest and drag a line to a target nest (neutral or enemy) to send troops.</li>
        <li><strong>Conquer Nests:</strong> Your nests spawn ants automatically over time. Overwhelm the defender's garrison to capture the nest.</li>
        <li><strong>Nest Upgrades:</strong> Nests upgrade automatically when troop counts hit milestones (Level 1 to 5). Level 2+ nests spawn powerful <strong>Champion Ants</strong> with 3x HP!</li>
      </ul>

      <h4>3 Ultimate Active Skills</h4>
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 1rem; margin-top: 1rem;">
        <div style="background: var(--bg-tertiary); padding: 1rem; border-radius: 8px; border: var(--border-glass);">
          <strong style="color: #ef4444;">🔥 Fire Strike (40 Mana)</strong>
          <p style="font-size: 0.85rem; color: var(--color-text-muted); margin-top: 0.5rem;">Ignites a march line, destroying marching enemy troops instantly.</p>
        </div>
        <div style="background: var(--bg-tertiary); padding: 1rem; border-radius: 8px; border: var(--border-glass);">
          <strong style="color: var(--color-accent);">⚡ Brood Surge (60 Mana)</strong>
          <p style="font-size: 0.85rem; color: var(--color-text-muted); margin-top: 0.5rem;">Increases nest spawn speed by +150% for 15 seconds.</p>
        </div>
        <div style="background: var(--bg-tertiary); padding: 1rem; border-radius: 8px; border: var(--border-glass);">
          <strong style="color: var(--color-secondary-accent);">🛡️ War Pheromone (60 Mana)</strong>
          <p style="font-size: 0.85rem; color: var(--color-text-muted); margin-top: 0.5rem;">Boosts damage and HP of all active troops for 15 seconds.</p>
        </div>
      </div>
    `,
  },
  {
    id: 'units',
    title: '🏰 12 Ant Classes & Unlocks',
    content: `
      <h3>12 Combat Ant Classes</h3>
      <div style="overflow-x: auto; margin-top: 1rem;">
        <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 0.9rem;">
          <thead>
            <tr style="background: var(--bg-tertiary); border-bottom: 2px solid var(--color-accent);">
              <th style="padding: 8px;">#</th>
              <th style="padding: 8px;">Ant Class</th>
              <th style="padding: 8px;">Specialties & Strengths</th>
              <th style="padding: 8px;">Unlock Requirement</th>
            </tr>
          </thead>
          <tbody>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">1</td><td><strong>Worker Ant</strong></td><td>Balanced, fast spawn rate</td><td>Default Unlocked</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">2</td><td><strong>Soldier Ant</strong></td><td>High HP and superior damage</td><td>Chapter 1, Stage 3</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">3</td><td><strong>Guard Ant</strong></td><td>Extreme HP, defensive line holder</td><td>Chapter 1, Stage 6</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">4</td><td><strong>Scout Ant</strong></td><td>Fastest movement speed</td><td>Chapter 1, Stage 9</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">5</td><td><strong>Venom Ant</strong></td><td>High damage with poison damage over time</td><td>Chapter 2, Stage 3</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">6</td><td><strong>Weaver Ant</strong></td><td>Impressive troop reproduction rate</td><td>Chapter 2, Stage 7</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">7</td><td><strong>Leafcutter Ant</strong></td><td>Balanced growth across stats</td><td>Chapter 3, Stage 1</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">8</td><td><strong>Artillery Ant</strong></td><td>High siege damage against enemy nests</td><td>Chapter 3, Stage 5</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">9</td><td><strong>Honeypot Ant</strong></td><td>Massive base health pool</td><td>Chapter 3, Stage 9</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">10</td><td><strong>Raider Ant</strong></td><td>Fast mobility, storm assault</td><td>Chapter 4, Stage 4</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">11</td><td><strong>Assassin Ant</strong></td><td>Extreme burst damage</td><td>Chapter 4, Stage 8</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">12</td><td><strong>Winged Ant</strong></td><td>Flies over obstacles, high mobility</td><td>Chapter 5, Stage 3</td></tr>
          </tbody>
        </table>
      </div>
    `,
  },
  {
    id: 'specs',
    title: '⚙️ 55 Campaign Levels',
    content: `
      <h3>55 Campaign Levels Across 5 Chapters</h3>
      <p>Each chapter contains 11 levels of escalating difficulty. Earn 3 stars by completing under 2 minutes!</p>
      
      <h4>Nest Levels & Capacity</h4>
      <ul>
        <li><strong>Level 1 Nest:</strong> Capacity 60 troops.</li>
        <li><strong>Level 2 Nest:</strong> Capacity 95 troops (Champion ants start spawning).</li>
        <li><strong>Level 3 Nest:</strong> Capacity 135 troops.</li>
        <li><strong>Level 4 Nest:</strong> Capacity 180 troops.</li>
        <li><strong>Level 5 Nest (Max):</strong> Capacity 300 troops, continuous Champion spawns.</li>
      </ul>
    `,
  },
];

export const antClashWikiDataVi = [
  {
    id: 'intro',
    title: '📖 Giới Thiệu & Bối Cảnh',
    content: `
      <h3>Tổng Quan Game Ant Clash</h3>
      <p><strong>Ant Clash</strong> là game chiến thuật thời gian thực (RTS) 2D nhịp độ nhanh. Người chơi chỉ huy đàn kiến đối đầu với đàn mối hung hãn để chiếm quyền kiểm soát toàn bộ các tổ trên chiến trường.</p>
      <div style="background: rgba(124, 58, 237, 0.15); border-left: 4px solid var(--color-primary); padding: 1rem; border-radius: 6px; margin: 1rem 0;">
        💡 <strong>Vòng lặp chơi chính:</strong><br/>
        <code>Chọn màn → Chọn loại kiến → Điều quân chiếm tổ → Tích nhựa & nhận thẻ → Nâng cấp đội quân</code>
      </div>
      <h4>Bối Cảnh Trận Đánh</h4>
      <p>Cuộc chiến diễn ra trong thế giới côn trùng thu nhỏ qua 5 môi trường độc đáo: Vườn sương sớm, Hang rễ cổ thụ, Bờ suối đá cuội, Cồn cát hoàng hôn và Pháo đài Kiến Chúa.</p>
    `,
  },
  {
    id: 'guide',
    title: '⚔️ Hướng Dẫn Chơi & Thao Tác',
    content: `
      <h3>Cách Điều Quân & Đọc Chiến Trường</h3>
      <ul>
        <li><strong>Mở tuyến hành quân:</strong> Chạm vào tổ của phe bạn, kéo đường sang tổ mục tiêu để xuất quân.</li>
        <li><strong>Chiếm tổ:</strong> Tổ của bạn tự sinh quân theo thời gian. Khi dồn quân quét sạch lực lượng đối phương, bạn sẽ chiếm được tổ.</li>
        <li><strong>Nâng cấp tổ:</strong> Tổ tự thăng cấp khi số quân tích lũy đạt mốc (Cấp 1 đến Cấp 5). Từ Cấp 2 trở lên, tổ sẽ tự động tạo tướng <strong>Champion</strong> mạnh gấp 3 lần máu!</li>
      </ul>
      <h4>3 Kỹ Năng Tối Thượng (Active Skills)</h4>
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 1rem; margin-top: 1rem;">
        <div style="background: var(--bg-tertiary); padding: 1rem; border-radius: 8px; border: var(--border-glass);">
          <strong style="color: #ef4444;">🔥 Hỏa Kích (40 Mana)</strong>
          <p style="font-size: 0.85rem; color: var(--color-text-muted); margin-top: 0.5rem;">Đốt cháy một tuyến đường, tiêu diệt quân địch đang di chuyển.</p>
        </div>
        <div style="background: var(--bg-tertiary); padding: 1rem; border-radius: 8px; border: var(--border-glass);">
          <strong style="color: var(--color-accent);">⚡ Cuồng Nộ Sinh Sản (60 Mana)</strong>
          <p style="font-size: 0.85rem; color: var(--color-text-muted); margin-top: 0.5rem;">Tăng +150% tốc độ đẻ quân cho toàn bộ tổ trong 15 giây.</p>
        </div>
        <div style="background: var(--bg-tertiary); padding: 1rem; border-radius: 8px; border: var(--border-glass);">
          <strong style="color: var(--color-secondary-accent);">🛡️ Pheromone Cuồng Chiến (60 Mana)</strong>
          <p style="font-size: 0.85rem; color: var(--color-text-muted); margin-top: 0.5rem;">Tăng mạnh sát thương và máu của toàn bộ quân trong 15 giây.</p>
        </div>
      </div>
    `,
  },
  {
    id: 'units',
    title: '🏰 12 Loại Kiến & Tiến Trình',
    content: `
      <h3>Bảng 12 Chủng Loại Kiến Chiến Đấu</h3>
      <div style="overflow-x: auto; margin-top: 1rem;">
        <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 0.9rem;">
          <thead>
            <tr style="background: var(--bg-tertiary); border-bottom: 2px solid var(--color-accent);">
              <th style="padding: 8px;">#</th>
              <th style="padding: 8px;">Tên Loại Kiến</th>
              <th style="padding: 8px;">Đặc Điểm & Điểm Mạnh</th>
              <th style="padding: 8px;">Điều Kiện Mở Khóa</th>
            </tr>
          </thead>
          <tbody>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">1</td><td><strong>Kiến Thợ</strong></td><td>Cân bằng, sinh quân nhanh</td><td>Mặc định có sẵn</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">2</td><td><strong>Kiến Lính</strong></td><td>Máu và sát thương vượt trội</td><td>Chương 1, Màn 3</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">3</td><td><strong>Kiến Hộ Vệ</strong></td><td>Máu cực cao, phòng thủ giữ tuyến tốt</td><td>Chương 1, Màn 6</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">4</td><td><strong>Kiến Trinh Sát</strong></td><td>Tốc độ di chuyển nhanh nhất</td><td>Chương 1, Màn 9</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">5</td><td><strong>Kiến Độc Châm</strong></td><td>Sát thương cao, khả năng độc tính</td><td>Chương 2, Màn 3</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">6</td><td><strong>Kiến Thợ Dệt</strong></td><td>Tốc độ sinh quân cực kỳ ấn tượng</td><td>Chương 2, Màn 7</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">7</td><td><strong>Kiến Mang Lá</strong></td><td>Tăng trưởng chỉ số đồng đều</td><td>Chương 3, Màn 1</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">8</td><td><strong>Kiến Pháo Binh</strong></td><td>Sát thương công thành cao</td><td>Chương 3, Màn 5</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">9</td><td><strong>Kiến Mật</strong></td><td>Bể máu nền rất lớn</td><td>Chương 3, Màn 9</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">10</td><td><strong>Kiến Đột Kích</strong></td><td>Di chuyển nhanh, tấn công bão táp</td><td>Chương 4, Màn 4</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">11</td><td><strong>Kiến Sát Thủ</strong></td><td>Sát thương sốc điện cực đại</td><td>Chương 4, Màn 8</td></tr>
            <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);"><td style="padding: 8px;">12</td><td><strong>Kiến Cánh</strong></td><td>Bay qua chướng ngại vật, cơ động cao</td><td>Chương 5, Màn 3</td></tr>
          </tbody>
        </table>
      </div>
    `,
  },
  {
    id: 'specs',
    title: '⚙️ 55 Màn Chơi & Kỹ Thuật',
    content: `
      <h3>Hệ Thống 55 Màn Chơi Qua 5 Chương</h3>
      <p>Mỗi chương bao gồm 11 màn chơi tăng dần độ khó. Thắng 3 sao nếu hoàn thành dưới 2 phút!</p>
      <h4>Cấp Độ Tổ & Sức Chứa Quân</h4>
      <ul>
        <li><strong>Tổ Cấp 1:</strong> Sức chứa 60 quân.</li>
        <li><strong>Tổ Cấp 2:</strong> Sức chứa 95 quân (Bắt đầu xuất hiện Tướng Champion).</li>
        <li><strong>Tổ Cấp 3:</strong> Sức chứa 135 quân.</li>
        <li><strong>Tổ Cấp 4:</strong> Sức chứa 180 quân.</li>
        <li><strong>Tổ Cấp 5 (Max):</strong> Sức chứa 300 quân, xuất hiện Champion liên tục.</li>
      </ul>
    `,
  },
];
